/**
 * AVBP Leads — the FRONT DOOR data model (Phase 1 · Front-door build).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE GAP THIS CLOSES
 * ─────────────────────────────────────────────────────────────────────────
 *  The rebuild started mid-journey (at the Project Conversation). Steps
 *  2–7 of the owner's canonical 14-step Client Journey Spine —
 *    1 Attract · 2 Enquire · 3 Qualify · 4 Book Consult · 5 Discovery
 *    Call · 6 Propose & Price · 7 Agreement & Payment · 8 Onboarding …
 *  — had NO entry point in the rebuild: a project could only exist if it
 *  was demo-seeded. This module is the pragmatic v1 front door:
 *
 *    lead enters  →  qualify  →  (book/discovery as status+date)  →
 *    pick tier → auto-quote  →  Convert to Project (seeds the existing
 *    Step-6 project/conversation/engagement/ledger pipeline).
 *
 *  FULL-FUNNEL-READY (additive, not a rewrite — explicitly):
 *    The `LeadState` enum already carries EVERY funnel state
 *    (enquired → qualifying → qualified → consult_booked →
 *    discovery_done → quoted → converted | parked | rejected) and the
 *    `Lead` shape already carries booking/discovery date fields + a typed
 *    `LeadInboundDraft` ingestion seam. v1 simply does not yet render a
 *    calendar UI for book/discovery (they are status+date fields). When
 *    the real funnel lands it fills these existing seams — it does NOT
 *    change the model, the state machine, the quote engine or the
 *    convert bridge. Nothing here is throw-away.
 *
 *  PURE + deterministic + dependency-light (only the LOCKED
 *  engagementTemplate config — pricing is NOT re-derived here). No
 *  Firebase, no React, so the Node native test runner exercises it
 *  directly via _tsload.cjs — exactly the Sprint-3/4 discipline. The
 *  Firestore read/write helpers live in conversation.ts / a thin leads
 *  data layer (described in the report; rules are described, not
 *  deployed, per the brief).
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  CORE_TIERS,
  getEngagementTemplate,
  isAncillaryTier,
  seedGateAmounts,
  type CoreTier,
  type Tier,
} from './engagementTemplate';
import { firmFor, type Currency, type FirmId } from './firms';
// Type-only imports (erased at compile — no runtime cycle with leadPipeline.ts):
// the Lenses pilot (AD-046) + L0 front door (AD-047) additive snapshot fields.
import type { LeadPipeline, FStage, L0Product } from './leadPipeline';

/* ════════════════════ Pipeline state (full-funnel-ready) ════════════════════ */

/**
 * THE full pipeline state machine. v1 only drives a subset of the
 * transitions through UI (book/discovery are status+date, no calendar),
 * but every state EXISTS so the later full funnel is purely additive.
 *
 * Linear spine:
 *   enquired → qualifying → qualified → consult_booked → discovery_done
 *            → quoted → converted
 * Terminal off-ramps from (almost) anywhere: parked · rejected.
 */
export type LeadState =
  | 'enquired' //        2 Enquire — landed, untouched
  | 'qualifying' //      3 Qualify — being triaged
  | 'qualified' //       3 Qualify — accepted, real opportunity
  | 'consult_booked' //  4 Book Consult — slot held (v1: date field)
  | 'discovery_done' //  5 Discovery Call — discovery captured (v1: date)
  | 'quoted' //          6 Propose & Price — tier picked, auto-quoted
  | 'converted' //       7 Agreement&Payment — became client+project
  | 'parked' //          off-ramp — not now, revisit later
  | 'rejected'; //       off-ramp — not a fit / lost

export const ALL_LEAD_STATES: LeadState[] = [
  'enquired',
  'qualifying',
  'qualified',
  'consult_booked',
  'discovery_done',
  'quoted',
  'converted',
  'parked',
  'rejected',
];

/** The linear spine order (off-ramps excluded) — drives the lead rail. */
export const LEAD_SPINE: LeadState[] = [
  'enquired',
  'qualifying',
  'qualified',
  'consult_booked',
  'discovery_done',
  'quoted',
  'converted',
];

export const LEAD_STATE_LABEL: Record<LeadState, string> = {
  enquired: 'Enquired',
  qualifying: 'Qualifying',
  qualified: 'Qualified',
  consult_booked: 'Consult booked',
  discovery_done: 'Discovery done',
  quoted: 'Quoted',
  converted: 'Converted',
  parked: 'Parked',
  rejected: 'Rejected',
};

/**
 * Allowed transitions. The off-ramps (park/reject) are reachable from any
 * non-terminal state; the spine moves forward one hop at a time; a parked
 * lead can re-enter at qualifying. `convert` is ONLY legal from `quoted`
 * (you cannot convert a lead with no priced tier — money-before-delivery
 * starts at conversion). This is the single authority on legality so the
 * UI never offers an illegal action.
 */
const TRANSITIONS: Record<LeadState, LeadState[]> = {
  enquired: ['qualifying', 'qualified', 'parked', 'rejected'],
  qualifying: ['qualified', 'parked', 'rejected'],
  qualified: ['consult_booked', 'discovery_done', 'quoted', 'parked', 'rejected'],
  consult_booked: ['discovery_done', 'quoted', 'parked', 'rejected'],
  discovery_done: ['quoted', 'parked', 'rejected'],
  quoted: ['converted', 'qualified', 'parked', 'rejected'],
  converted: [], // terminal — the project pipeline takes over
  parked: ['qualifying', 'rejected'],
  rejected: [], // terminal
};

export function canTransition(from: LeadState, to: LeadState): boolean {
  return TRANSITIONS[from]?.includes(to) ?? false;
}

export function nextStates(from: LeadState): LeadState[] {
  return TRANSITIONS[from] ?? [];
}

export function isTerminal(s: LeadState): boolean {
  return TRANSITIONS[s].length === 0;
}

/* ════════════════════ Source (the 3 entry paths) ════════════════════ */

/**
 * v1 collapses the (later) 15-source taxonomy to the owner-named entry
 * PATHS the front door must visibly support now. The full source list is
 * additive later (the legacy `src/_legacy/types/lead.ts` 15-value enum is
 * the reference; v1 does not need it yet). Each path maps cleanly into
 * one of these — no information is lost, the model just isn't granular
 * yet.
 *
 * `whatsapp` was added 2026-05-22 (bug fix — hidden-leads): inbound
 * WhatsApp messages from an unknown number are auto-written to `leads/`
 * by `firebase-functions/avbp_wa_router.js` with `source:'whatsapp_inbound'`.
 * That channel must surface in the Lead Inbox, so it gets a first-class
 * `LeadSource` value rather than being collapsed into `manual_referral`.
 */
export type LeadSource =
  | 'website'
  | 'free_tool'
  | 'manual_referral'
  | 'whatsapp';

export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  website: 'Website form',
  free_tool: 'Free tool',
  manual_referral: 'Manual / referral',
  whatsapp: 'WhatsApp inbound',
};

export const ALL_LEAD_SOURCES: LeadSource[] = [
  'website',
  'free_tool',
  'manual_referral',
  'whatsapp',
];

/* ════════════════════ The Lead ════════════════════ */

export interface LeadQuote {
  /** The tier the quote is for (locked). */
  tier: Tier;
  /** Locked Modern + Sanskrit displayName (engagementTemplate). */
  displayName: string;
  /** Total engagement value in INR — auto-priced (see autoQuote). */
  valueINR: number;
  /**
   * How the figure was reached, in plain language, so staff/owner can
   * see it is the locked figure and not invented. Cited to the tier.
   */
  basis: string;
  /** sqft input used (computed tiers only); null for fixed-fee tiers. */
  sqft: number | null;
}

export interface Lead {
  /** Stable lead id (LD-####). */
  id: string;
  source: LeadSource;
  state: LeadState;

  /* — contact (the enquiry) — */
  name: string;
  phone: string;
  email: string;
  /** City / area free text — enough for v1 triage. */
  location: string;

  /* — the concern / property basics (captured at qualify) — */
  concern: string;
  /** residence | commercial | … free text in v1 (picklist later). */
  propertyType: string;
  /** Total built-up sqft if known — feeds computed-tier auto-quote. */
  sqft: number | null;

  /* — tier + quote (Propose & Price) — */
  /** Tier the client/staff is leaning toward (pre-quote). */
  desiredTier: Tier | null;
  /** Tier actually quoted/assigned. */
  assignedTier: Tier | null;
  quote: LeadQuote | null;

  /* — booking / discovery (v1 = status + date fields; no calendar UI.
       The full funnel fills these existing seams — additive) — */
  consultBookedAt: string | null; // ISO date
  discoveryDoneAt: string | null; // ISO date

  /* ── REAL consult booking (Phase 2, 2026-07-03) — set by the
   * createConsultBooking callable path (consultStore.bookConsultation →
   * applyConsultBookedToLead). Like every additive lead field these ride
   * INSIDE the `notes_internal` JSON snapshot (leadStore serialises the whole
   * Lead), so they round-trip with NO firestore.rules change; legacy leads
   * (absent fields) are untouched. */
  /** Google Meet link minted by the booking callable (video consults). */
  consultMeetLink?: string | null;
  /** consultations/{id} doc minted by the booking callable (audit / joins). */
  consultationId?: string | null;

  /* ── Call outcome notes (Phase 2c, 2026-07-03) — captured with "Discovery
   * done": what was discussed / minutes of the discovery call. THE input the
   * customized quote is built from — the quote composer surfaces it when it
   * opens pre-filled from this lead. Snapshot-carried (no rules change). */
  callNotes?: string | null;

  /* ── Next-action engine (Phase 3a, 2026-07-03) — the ONE follow-up pointer
   * per lead, auto-set on stage entry by the state-machine event seams (see
   * `nextActionForState` / `withNextAction`) and surfaced as the "Needs
   * attention" strip when overdue. Snapshot-carried (no rules change). */
  /** When the next action is due (ISO). null = nothing pending (e.g. booked/terminal). */
  nextActionAt?: string | null;
  /** Plain-language what-to-do, e.g. "Hi call now — new enquiry". */
  nextActionNote?: string | null;

  /* — ownership + audit — */
  ownerName: string;
  assigneeUid: string | null;
  assigneeName: string | null;
  notes: string;
  createdAt: string; // ISO
  updatedAt: string; // ISO

  /* ── AGREED PRICE / SEND (quote-redesign 2026-06-15) ──
   *
   * The redesigned lead→price→convert flow (MOCKUP_1/2/5). "Whatever price
   * you agree IS what the client pays" — these carry the ONE agreed figure
   * straight onto the lead so Convert seeds the money gate with it (reusing
   * the agreed-price plumbing: engagementTemplate.seedGateAmounts). All
   * OPTIONAL so every legacy lead round-trips unchanged (they live inside the
   * `notes_internal` JSON snapshot — no Firestore rules change).
   *
   * The agreed price is the SINGLE source of truth for the lead's price; the
   * legacy `quote.valueINR` (auto-quote) is kept only as the rate-card
   * "standard price" reference. When `agreedValue` is set it overrides it. */
  /** Currency the price is agreed in ('INR' | 'USD'). */
  agreedCurrency?: 'INR' | 'USD' | null;
  /** The agreed amount in `agreedCurrency` units (e.g. 40 for $40). */
  agreedValue?: number | null;
  /** Does the client need a GST invoice? (routes the billing firm). */
  gstRequired?: boolean | null;
  /** Billing firm — auto-routed from {currency, gst}, admin-overridable. */
  firmId?: 'firm1' | 'firm2' | null;
  /** "No charge" engagement (family / referral / goodwill). */
  noCharge?: boolean;
  /** One-line reason when noCharge is true. */
  noChargeReason?: string | null;
  /** When the quote was last sent to the client (ISO), null if never. */
  sentAt?: string | null;
  /** Channels the quote was sent on ('whatsapp' | 'email'), audit. */
  sentChannels?: string[] | null;

  /* — conversion bridge (set when state → converted) — */
  /** The VP project id minted on Convert-to-Project. */
  convertedProjectId: string | null;
  /** The C##### client number minted on Convert. */
  convertedClientNo: string | null;

  /* ── Contact spine (AD-039, 2026-06-24) — the canonical identity link ──
   *
   * The ONE link from a lead to a `clients/{cid}` Person Data Hub. Like
   * `trashed`/`agreedValue`/`submission`, these ride INSIDE the
   * `notes_internal` JSON snapshot (`JSON.stringify(lead)` in leadStore),
   * so they round-trip with NO firestore.rules change. `null` until the lead
   * is matched/converted, so every legacy lead round-trips unchanged.
   *
   * AUTO-link is ONLY on an exact phone/email match (contactMatch.bestAutoLink);
   * a weaker score is a staff-confirm SUGGESTION surfaced via `matchCandidates`,
   * never a silent link. */
  /** clients/{cid} this lead resolves to, when known. null until matched/converted. */
  contactId?: string | null;
  /** How the link was made (audit). */
  contactLinkSource?: 'matched' | 'manual' | 'converted' | null;
  /** Top dedup candidates surfaced at creation (ids+score only) for the staff-confirm chip. */
  matchCandidates?: Array<{ contactId: string; clientNo: string; name: string; score: number }> | null;
  /** Candidate ids the staff dismissed, so the chip doesn't nag. */
  dismissedMatchIds?: string[] | null;

  /* ── Recoverable Trash (2026-05-22, owner decision) ──
   *
   * A trashed lead is sent to a recoverable Trash, NOT hard-deleted. It
   * vanishes from every normal Lead Inbox query/count/picker but stays a
   * real `leads/{id}` doc, restorable from the Trash view, until an admin
   * permanently purges it via the `purgeTrashedRecord`/`emptyTrash` CF.
   *
   * `trashed` is part of the pure `Lead` model on purpose: the `leads/`
   * Firestore rule whitelist (firebase-functions/firestore.rules) does NOT
   * permit a top-level `trashed` field even for an admin update — but it
   * DOES whitelist `notes_internal`, where leadStore serialises this whole
   * `Lead` snapshot. So `trashed` round-trips inside the snapshot with no
   * rules change. `leadStore.docToLead` reads it back; `listenLeads`
   * filters on it. Distinct from `parked`/`rejected` — those are pipeline
   * states a lead can come BACK from into the spine; trash is a removal.
   */
  /** true → the lead is in the recoverable Trash (hidden from all lists). */
  trashed?: boolean;
  /** ISO timestamp the lead was sent to Trash. */
  trashedAt?: string | null;
  /** Actor name who sent the lead to Trash (audit). */
  trashedBy?: string | null;

  /* ── Submission payload — "what the client told us" (2026-06-21) ──
   *
   * Website / free-tool leads land as FLAT `leads/{id}` docs carrying the
   * client's own answers (contact message, E2 score + observations + room
   * zones + uploaded map, E4 area scores + flags, …). The pure pipeline
   * does not need these to triage, so the rest of the model ignores them —
   * but the staffer DOES need to see them on the lead record. `leadStore.
   * flatDocToLead` extracts them off the raw doc into this OPTIONAL carry
   * field (`lib/leadSubmission.extractSubmission`); `SubmissionDetails.tsx`
   * renders it. It round-trips inside the `notes_internal` snapshot — no
   * firestore.rules change. Absent for portal-native / WhatsApp leads. The
   * type is intentionally referenced loosely (the field is display-only and
   * never feeds the state machine / quote engine). */
  submission?: import('./leadSubmission').LeadSubmission | null;

  /* ── Lenses pilot (AD-046, 2026-06-26) — additive view-layer fields ──
   *
   * The Leads "Lenses" multi-view layer discriminates a SECOND (Fiverr)
   * pipeline alongside the live `state` machine. Both fields ride INSIDE the
   * `notes_internal` JSON snapshot (like `agreedValue`/`trashed`) — NO
   * firestore.rules change, NO new collection. All OPTIONAL so every legacy
   * lead round-trips unchanged: absent `leadPipeline` → 'standard'; absent
   * `fstage` → not on the Fiverr board. They are NOT queryable (snapshot
   * fields) — every Lens view filters in-memory off the one `listenLeads`
   * stream, so that is fine. See `lib/leadPipeline.ts`. */
  /** Which pipeline a lead is on. Default (absent) → 'standard'. */
  leadPipeline?: LeadPipeline | null;
  /** Fiverr order stage — only meaningful when leadPipeline === 'fiverr'. */
  fstage?: FStage | null;

  /* ── L0 front door (AD-047, 2026-06-26) — automated lead-magnet attribution ──
   *
   * An L0 lead (a website free-magnet submission or the free Astro-Vastu
   * report) keeps its live top-level `source` value (so the 5-source lens
   * reading is intact) and adds a finer SUB-TAG here — never a new top-level
   * source. Plus the OPTIONAL ₹599 consultation upsell flag set from the
   * Razorpay webhook (the report itself is FREE; the ₹599 is a post-report
   * consultation, manual booking v1 — owner-locked 2026-06-26). All snapshot
   * fields, no rules change. */
  /** Which automated L0 product produced this lead (finer than `source`). */
  l0Product?: L0Product | null;
  /** true → the optional ₹599 post-report consultation was paid (Razorpay). */
  consultationPaid?: boolean | null;
  /** ISO timestamp the consultation payment cleared. */
  consultationPaidAt?: string | null;
  /** Amount paid for the consultation (units of consultationCurrency). */
  consultationAmount?: number | null;
  /** Currency of the consultation payment. */
  consultationCurrency?: 'INR' | 'USD' | null;

  /* ── State-correction audit (2026-07-03, streamline Phase 1b) ──
   *
   * When staff CORRECT a lead's stage out-of-flow (a backward/lateral board
   * drag the state machine does not allow), the write goes through
   * `correctLeadState` — never a raw `state` write — and this marker records
   * it. Optional + snapshot-carried (notes_internal), so every legacy lead
   * round-trips unchanged and no firestore.rules change is needed. */
  /** The most recent out-of-flow state correction (audit marker). */
  lastStateCorrection?: {
    from: LeadState;
    to: LeadState;
    at: string; // ISO
  } | null;

  /* ── Conversation join (AD-039, 2026-07-03 streamline Phase 1 integration) ──
   *
   * The lead → Team-Inbox-thread ref the identity join (lib/leadThreadJoin)
   * computes from an EXACT phone match. Persisting it makes the join sticky:
   * subsequent loads prefer the stored key (falling back to a fresh computed
   * match if the stored thread vanished). SNAPSHOT fields — they ride inside
   * the `notes_internal` JSON snapshot like every other additive Lead field,
   * so NO firestore.rules change and every legacy lead round-trips unchanged.
   * The thread→lead half stays computed (wa_conversations is server-only). */
  /** Conversation key of the joined WhatsApp thread (`/messages?c=<key>`). */
  waThreadKey?: string | null;
  /** How the thread link was made: 'matched' = exact-identity auto-join. */
  threadLinkSource?: 'matched' | 'manual' | null;

  /* ── Conversion claim (2026-07-03, Vex gate F2) ──
   *
   * The DURABLE reservation that serialises accept-driven conversion: before
   * any project is minted, the accepting run stamps its quote id here inside
   * a Firestore TRANSACTION (leadStore.claimLeadConversionTx). A concurrent
   * second run (two tabs / a retry racing a slow first run) loses the
   * transaction and refuses — so at most one run mints. TTL-bounded so a
   * crashed run never bricks the lead. Snapshot-carried (notes_internal) —
   * no firestore.rules change, legacy leads round-trip unchanged. */
  /** The quote id currently holding the conversion claim. */
  convertClaimId?: string | null;
  /** ISO timestamp the claim was taken (drives the TTL). */
  convertClaimAt?: string | null;
}

/* ════════════════════ Inbound ingestion seam (full-funnel-ready) ════════════════════ */

/**
 * The typed seam every inbound channel funnels through. v1 has 3 callers:
 *  - the "+ New Lead" form (manual_referral)
 *  - the demo "simulate website form" affordance (website)
 *  - the demo "simulate free-tool result" affordance (free_tool)
 *
 * The REAL website-form / free-tool ingestion (a Cloud Function writing
 * `leads/{id}`) drops in HERE later with zero model change — it produces
 * the same `LeadInboundDraft`. This mirrors the Sprint-2 extractor seam
 * and the Sprint-4 TopicMatcher seam: one marked insertion point.
 */
export interface LeadInboundDraft {
  source: LeadSource;
  name: string;
  phone?: string;
  email?: string;
  location?: string;
  concern?: string;
  propertyType?: string;
  sqft?: number | null;
  desiredTier?: Tier | null;
  ownerName?: string;
  notes?: string;
  /* ── Contact spine (AD-039) — match-at-creation seam ──
   * The lead-create caller runs `contactMatch` against the live `clients`
   * snapshot first; an exact-phone/email auto-link passes `contactId` here,
   * and the top suggestions ride in `matchCandidates` for the staff chip. */
  contactId?: string | null;
  matchCandidates?: Array<{ contactId: string; clientNo: string; name: string; score: number }> | null;
}

/**
 * Deterministic id mint — caller supplies a monotone seq so tests/seed
 * are reproducible (no Date.now / random in the pure layer; the Firestore
 * layer supplies a real seq). Format LD-#### (4-digit padded) — a
 * portal-native serial discipline.
 */
export function leadId(seq: number): string {
  return `LD-${String(seq).padStart(4, '0')}`;
}

/**
 * Build a fresh Lead from an inbound draft. Pure: the caller supplies the
 * id + an ISO `now` (the data layer passes a real serverTimestamp-derived
 * value; tests pass a fixed string) so this stays deterministic. Every
 * lead starts at `enquired` — the very first spine state — regardless of
 * channel. Booking/discovery/quote seams start empty (filled later).
 */
export function makeLead(
  id: string,
  draft: LeadInboundDraft,
  now: string,
): Lead {
  return {
    id,
    source: draft.source,
    state: 'enquired',
    name: draft.name.trim(),
    phone: draft.phone?.trim() ?? '',
    email: draft.email?.trim() ?? '',
    location: draft.location?.trim() ?? '',
    concern: draft.concern?.trim() ?? '',
    propertyType: draft.propertyType?.trim() ?? '',
    sqft: draft.sqft ?? null,
    desiredTier: draft.desiredTier ?? null,
    assignedTier: null,
    quote: null,
    consultBookedAt: null,
    discoveryDoneAt: null,
    ownerName: draft.ownerName?.trim() || 'Unassigned',
    assigneeUid: null,
    assigneeName: null,
    notes: draft.notes?.trim() ?? '',
    createdAt: now,
    updatedAt: now,
    convertedProjectId: null,
    convertedClientNo: null,
    // Contact spine (AD-039) — an exact-match auto-link stamps contactId at
    // creation; otherwise contact-less until matched/converted. The top
    // suggestions ride for the staff-confirm chip.
    contactId: draft.contactId ?? null,
    contactLinkSource: draft.contactId ? 'matched' : null,
    matchCandidates: draft.matchCandidates ?? null,
    dismissedMatchIds: null,
    // Phase 3a — a brand-new enquiry OWES its first touch immediately
    // ("Hi call now", due now). Same engine every stage entry uses.
    ...nextActionForState('enquired', now),
  };
}

/* ════════════════════ Auto-quote (LOCKED pricing — NOT re-derived) ════════════════════ */

/**
 * Per-tier v1 baseline pricing. EVERY figure traces to the owner-locked
 * FULLSCOPE_{tier} §"Charge model" + LADDER_DECISIONS_LOCKED, all dated
 * 2026-05-16 (Owner's Inbox/01_Sessions/). Pricing is NOT invented here
 * and NOT re-derived from training data — it is the locked figure:
 *
 *   FIXED-fee tiers (sqft-independent):
 *     · L1A  ₹11,000  — FULLSCOPE_L1A §2/§6 (100% advance)
 *     · L1B  ₹11,000  — FULLSCOPE_L1B §2/§6 (100% advance)
 *     · L3A  ₹11,000  — FULLSCOPE_L3A §2 (₹11,000 / $120, 100% advance)
 *     · L2B  ₹51,000  — FULLSCOPE_L2B §2 (₹31,000 + ₹20,000 two-report)
 *     · L2C  ₹40,000  — FULLSCOPE_L2C §"Charge model" (₹20,000 × 2 at the
 *                       ≤1,500 sqft residence baseline)
 *
 *   COMPUTED tiers (rate × sqft, LADDER taper band):
 *     · L2A  ₹20,000 / 1,000 sqft  — FULLSCOPE_L2A (ref ₹60,000 ≈ 3,000
 *                       sqft); v1 default sqft 3,000 → ₹60,000
 *     · L2D  ₹40,000 / 1,000 sqft  — FULLSCOPE_L2D §"Charge model"
 *                       (half of L3B); v1 default sqft 1,500
 *     · L3B  ₹80,000 / 1,000 sqft / floor — FULLSCOPE_L3B §3.1; v1
 *                       default sqft 1,500, ground floor (×1)
 *
 *   LADDER cross-tier sqft band (applied to computed tiers, on TOTAL
 *   project sqft): first 5,000 = 100% · 5,001–10,000 = 80% · >10,000 =
 *   60% of base rate. v1 floor multiplier = ground (×1); the full
 *   per-floor multiplier is additive later (the model already carries
 *   sqft; floors join the same seam).
 *
 *  v1 SCOPE NOTE (not a gap — a stated v1 simplification): L2A/L2D/L3B
 *  per-FLOOR pricing and the international ($) path are out of v1 front
 *  door scope and fold in additively at the full-funnel sprint. The
 *  figures above are the LOCKED domestic single-floor baselines. This is
 *  explicitly the pragmatic v1 the brief asks for.
 */
interface TierPricing {
  kind: 'fixed' | 'computed';
  /** fixed → the total INR. computed → INR per 1,000 sqft. */
  rateINR: number;
  /** computed tiers: default sqft when the lead has none. */
  defaultSqft?: number;
  /** Plain-language basis string (cited). */
  basis: string;
}

// Lead-funnel pricing covers the 8 CORE tiers only. Ancillary jobs
// (Site Visit / Survey / Map Grading) are priced by lib/ancillary —
// they are attached to a project or sold standalone, never quoted
// through the lead-triage funnel.
const PRICING: Record<CoreTier, TierPricing> = {
  L1A: {
    kind: 'fixed',
    rateINR: 11000,
    basis: '₹11,000 fixed · 100% advance (FULLSCOPE_L1A §2/§6)',
  },
  L1B: {
    kind: 'fixed',
    rateINR: 11000,
    basis: '₹11,000 fixed · 100% advance (FULLSCOPE_L1B §2/§6)',
  },
  L1C: {
    kind: 'fixed',
    rateINR: 11000,
    basis: '₹11,000 fixed · 100% advance · plan review (L1C_L2C_AVBP_INTEGRATION §6.2)',
  },
  L2A: {
    kind: 'computed',
    rateINR: 20000,
    defaultSqft: 3000,
    basis:
      '₹20,000 / 1,000 sqft (FULLSCOPE_L2A — ref ₹60,000 ≈ 3,000 sqft; LADDER taper band)',
  },
  L2B: {
    kind: 'fixed',
    rateINR: 51000,
    basis:
      '₹51,000 fixed · ₹31,000 R1 + ₹20,000 R2 two-report (FULLSCOPE_L2B §2)',
  },
  L2C: {
    kind: 'fixed',
    rateINR: 40000,
    basis:
      '₹40,000 · ₹20,000 × 2 at ≤1,500 sqft residence baseline (FULLSCOPE_L2C Charge model)',
  },
  L2D: {
    kind: 'computed',
    rateINR: 40000,
    defaultSqft: 1500,
    basis:
      '₹40,000 / 1,000 sqft (FULLSCOPE_L2D Charge model; LADDER taper band; ground-floor v1)',
  },
  L3A: {
    kind: 'fixed',
    rateINR: 11000,
    basis: '₹11,000 fixed · 100% advance (FULLSCOPE_L3A §2)',
  },
  L3B: {
    kind: 'computed',
    rateINR: 80000,
    defaultSqft: 1500,
    basis:
      '₹80,000 / 1,000 sqft / floor (FULLSCOPE_L3B §3.1; LADDER taper band; ground-floor v1)',
  },
};

/**
 * LADDER cross-tier sqft taper band, applied to TOTAL project sqft.
 * first 5,000 = 100% · 5,001–10,000 = 80% · >10,000 = 60% of base rate.
 * Pure piecewise — deterministic, returns the blended INR for the rate.
 */
function bandedComputed(ratePer1000: number, sqft: number): number {
  const tier1 = Math.min(sqft, 5000);
  const tier2 = Math.min(Math.max(sqft - 5000, 0), 5000);
  const tier3 = Math.max(sqft - 10000, 0);
  const perSqft = ratePer1000 / 1000;
  const total =
    tier1 * perSqft + tier2 * perSqft * 0.8 + tier3 * perSqft * 0.6;
  return Math.round(total);
}

/**
 * THE auto-quote. Given a tier (+ optional sqft for computed tiers),
 * returns the LOCKED engagement value. Fixed tiers ignore sqft entirely;
 * computed tiers use the lead's sqft, else the tier default, then apply
 * the LADDER band. The returned `valueINR` is the SAME figure the
 * existing `moneyViewOf` / engagementTemplate gate splitter consumes on
 * Convert — so the front-door quote and the post-conversion money gates
 * are guaranteed consistent (no second pricing path).
 */
export function autoQuote(tier: Tier, sqft?: number | null): LeadQuote {
  const tpl = getEngagementTemplate(tier);
  if (!tpl) {
    throw new Error(`autoQuote: unknown tier "${tier}"`);
  }
  if (isAncillaryTier(tier)) {
    // Ancillary jobs are priced by lib/ancillary (mode/scenario/per-floor),
    // not the lead funnel. The lead-triage picker only offers core tiers.
    throw new Error(
      `autoQuote: "${tier}" is an ancillary tier — price via lib/ancillary, not the lead funnel`,
    );
  }
  const p = PRICING[tier];
  if (p.kind === 'fixed') {
    return {
      tier,
      displayName: tpl.displayName,
      valueINR: p.rateINR,
      basis: p.basis,
      sqft: null,
    };
  }
  const useSqft =
    sqft != null && sqft > 0 ? Math.round(sqft) : p.defaultSqft!;
  return {
    tier,
    displayName: tpl.displayName,
    valueINR: bandedComputed(p.rateINR, useSqft),
    basis: `${p.basis} · ${useSqft.toLocaleString('en-IN')} sqft`,
    sqft: useSqft,
  };
}

/** Is this tier a fixed-fee tier (sqft has no effect)? UI hint.
 *  Ancillary tiers have no lead-funnel price → false (not a fixed-fee
 *  core tier). */
export function isFixedFeeTier(tier: Tier): boolean {
  if (isAncillaryTier(tier)) return false;
  return PRICING[tier].kind === 'fixed';
}

/* ════════════════════ Lead mutations (pure) ════════════════════ */

/**
 * Transition a lead's state, returning a NEW lead (never mutates — same
 * immutability discipline as projectLedger's reducers; the Firestore
 * layer persists the result + appends an audit row). Throws on an illegal
 * transition so an illegal action can never reach the data layer.
 */
export function transitionLead(
  lead: Lead,
  to: LeadState,
  now: string,
): Lead {
  if (!canTransition(lead.state, to)) {
    throw new Error(
      `Illegal lead transition ${lead.state} → ${to} (lead ${lead.id})`,
    );
  }
  // Phase 3a — EVERY stage entry re-derives the lead's next action through
  // the one engine (nextActionForState). Because this fn is one of only two
  // writers of `lead.state`, no lead can enter an open stage without its
  // follow-up pointer.
  return withNextAction({ ...lead, state: to, updatedAt: now }, now);
}

/* ════════════════════ Next-action engine (Phase 3a, 2026-07-03) ═══════════
 *
 * ONE follow-up pointer per lead — `nextActionAt` (when it's due) +
 * `nextActionNote` (plain-language what-to-do) — AUTO-SET on every stage
 * entry through the one state machine (transitionLead / correctLeadState /
 * makeLead) plus the quote-sent event (which can refresh `quoted` without a
 * hop). Overdue (nextActionAt < now) leads surface in the "Needs attention"
 * strip on /leads and Home.
 *
 * THE INVARIANT (owner rule): no lead in an open stage ends up without a
 * next action. Two deliberate exceptions, both "the future is already
 * scheduled / deliberately paused":
 *   · consult_booked — the consult ITSELF is the next step (consultBookedAt
 *     carries its date); a second nag on top would be noise. CLEARED per the
 *     Phase-3 spec ("consult booked → clear").
 *   · parked — an explicit off-ramp: the staff chose "not now".
 * Terminals (converted / rejected) clear — the pipeline is done with them.
 */

export interface NextAction {
  nextActionAt: string | null;
  nextActionNote: string | null;
}

/** PURE — the next action a lead OWES on entering `state` (nowISO = entry time). */
export function nextActionForState(state: LeadState, nowISO: string): NextAction {
  const plusDays = (n: number) =>
    new Date(Date.parse(nowISO) + n * 86_400_000).toISOString();
  switch (state) {
    case 'enquired':
      return { nextActionAt: nowISO, nextActionNote: 'Hi call now — new enquiry' };
    case 'qualifying':
      return {
        nextActionAt: plusDays(1),
        nextActionNote: 'Finish qualifying — capture the concern & property basics',
      };
    case 'qualified':
      return {
        nextActionAt: nowISO,
        nextActionNote: 'Book the consult (or send the quote)',
      };
    case 'consult_booked':
      // the consult IS the scheduled next step — no second nag (spec: clear).
      return { nextActionAt: null, nextActionNote: null };
    case 'discovery_done':
      return { nextActionAt: nowISO, nextActionNote: 'Send the customized quote' };
    case 'quoted':
      return {
        nextActionAt: plusDays(2),
        nextActionNote: 'Follow up on the sent quote',
      };
    default:
      // parked (deliberate pause) + terminals (converted / rejected).
      return { nextActionAt: null, nextActionNote: null };
  }
}

/** PURE — stamp the state's next action onto the lead (the one applier). */
export function withNextAction(lead: Lead, nowISO: string): Lead {
  const na = nextActionForState(lead.state, nowISO);
  return { ...lead, nextActionAt: na.nextActionAt, nextActionNote: na.nextActionNote };
}

/** PURE — the leads OVERDUE on their next action (the "Needs attention"
 *  strip's source): open follow-up due at/before now, trash excluded,
 *  oldest-due first. Defensive on garbage dates. */
export function needsAttention(leads: Lead[], nowISO: string): Lead[] {
  const now = Date.parse(nowISO);
  return (Array.isArray(leads) ? leads : [])
    .filter((l) => {
      if (!l || l.trashed) return false;
      if (typeof l.nextActionAt !== 'string' || !l.nextActionAt) return false;
      const t = Date.parse(l.nextActionAt);
      return Number.isFinite(t) && t <= now;
    })
    .sort((a, b) => Date.parse(a.nextActionAt as string) - Date.parse(b.nextActionAt as string));
}

/**
 * OUT-OF-FLOW STATE CORRECTION (2026-07-03, streamline Phase 1b).
 *
 * `transitionLead` and `correctLeadState` are the ONLY two writers of
 * `lead.state` — nothing else may set it. A correction is an explicit,
 * staff-confirmed move the spine does not allow (a backward board drag,
 * an off-ramp fix). It:
 *   · validates the target is a real state and actually different;
 *   · REFUSES `converted` as a target — conversion mints a client + project
 *     and must go through the convert bridge, never a drag;
 *   · stamps `lastStateCorrection` {from,to,at} so the move is auditable
 *     (the persist layer's activity line marks it "(correction)" too);
 *   · preserves every other timestamp/field — nothing is cleared, so a
 *     forward re-move later finds the lead's history intact.
 */
export function correctLeadState(
  lead: Lead,
  to: LeadState,
  now: string,
): Lead {
  if (!ALL_LEAD_STATES.includes(to)) {
    throw new Error(`correctLeadState: unknown state "${to}" (lead ${lead.id})`);
  }
  if (to === lead.state) {
    throw new Error(
      `correctLeadState: lead ${lead.id} is already '${to}' — nothing to correct`,
    );
  }
  if (to === 'converted') {
    throw new Error(
      `correctLeadState: cannot mark lead ${lead.id} converted by correction — ` +
        `use Convert (it creates the client + project)`,
    );
  }
  // Phase 3a — a correction is a stage entry too: the corrected stage's next
  // action replaces the old one (same engine as transitionLead).
  return withNextAction(
    {
      ...lead,
      state: to,
      updatedAt: now,
      lastStateCorrection: { from: lead.state, to, at: now },
    },
    now,
  );
}

/* ════════════════════ Conversion claim (pure — Vex gate F2) ════════════════════
 *
 *  The pure half of the durable convert reservation. `leadStore.
 *  claimLeadConversionTx` runs these inside a Firestore transaction so two
 *  overlapping accept runs can never both mint a project. TTL-bounded: a
 *  claim older than CONVERT_CLAIM_TTL_MS is stale (its run died) and may be
 *  taken over — the taker FIRST looks for a project the dead run may have
 *  already minted (projects.sourceLeadId) and resumes it instead of minting.
 */

/** How long a conversion claim stays live before it is considered dead. */
export const CONVERT_CLAIM_TTL_MS = 5 * 60_000;

/** PURE — is there a live (non-stale) conversion claim on this lead? */
export function conversionClaimIsLive(lead: Lead, nowISO: string): boolean {
  if (!lead.convertClaimId || !lead.convertClaimAt) return false;
  const at = Date.parse(lead.convertClaimAt);
  const now = Date.parse(nowISO);
  if (Number.isNaN(at) || Number.isNaN(now)) return true; // unreadable → treat as live (fail-closed)
  return now - at < CONVERT_CLAIM_TTL_MS;
}

/** PURE — stamp the conversion claim. Throws on an already-converted lead. */
export function claimConversion(lead: Lead, claimId: string, now: string): Lead {
  if (lead.state === 'converted' || lead.convertedProjectId) {
    throw new Error(`claimConversion: lead ${lead.id} is already converted`);
  }
  return { ...lead, convertClaimId: claimId, convertClaimAt: now, updatedAt: now };
}

/** PURE — release the conversion claim (accept refused / convert failed). */
export function releaseConversionClaim(lead: Lead, now: string): Lead {
  return { ...lead, convertClaimId: null, convertClaimAt: null, updatedAt: now };
}

/**
 * PURE — RECOVERY ONLY: re-attach a lead to a project a previous (crashed)
 * conversion run already minted for it, without minting anything new. Mirrors
 * the convert bridge's own stamping exactly (`convertLeadDirect` forces
 * 'quoted' then `transitionLead` → 'converted'), stamps the bridge ids and
 * clears the claim. Never used on a fresh conversion — `convertLeadToProject`
 * remains THE bridge.
 */
export function resumeLeadConversion(
  lead: Lead,
  projectId: string,
  clientNo: string,
  now: string,
): Lead {
  const base: Lead = {
    ...releaseConversionClaim(lead, now),
    convertedProjectId: projectId,
    convertedClientNo: clientNo,
  };
  if (base.state === 'converted') return base;
  // Same forced hop the bridge itself performs — the ONE state machine.
  return transitionLead({ ...base, state: 'quoted' }, 'converted', now);
}

/* ════════════════════ Event-driven stage moves (pure seams) ════════════════════
 *
 *  2026-07-03 streamline Phase 1b — real pipeline EVENTS advance the lead
 *  through the ONE state machine instead of a human remembering. Each seam
 *  is a pure multi-hop walk: every hop is a LEGAL `transitionLead` call, so
 *  the spine (and its audit) stays the single authority. Terminal states
 *  are never regressed.
 */

/** The next legal hop from each state on the way to `quoted`. */
const HOP_TOWARD_QUOTED: Partial<Record<LeadState, LeadState>> = {
  enquired: 'qualified',
  qualifying: 'qualified',
  qualified: 'quoted',
  consult_booked: 'quoted',
  discovery_done: 'quoted',
  parked: 'qualifying',
};

/**
 * EVENT: a real quote was SENT to the client (the /quotes send flow).
 * Advances the lead to `quoted` via legal spine hops (a qualifying lead
 * walks qualifying→qualified→quoted; a parked lead re-enters first) and
 * stamps `sentAt`. Already `quoted` → only the send stamp refreshes.
 * Terminal (`converted`/`rejected`) → returned UNCHANGED (never regress /
 * never resurrect via an event). Pure — the impure quote-send seam persists.
 */
export function advanceLeadOnQuoteSent(
  lead: Lead,
  now: string,
  channels?: string[] | null,
): Lead {
  if (isTerminal(lead.state)) return lead;
  let next: Lead = lead;
  let safety = 0;
  while (next.state !== 'quoted' && safety < 4) {
    const hop = HOP_TOWARD_QUOTED[next.state];
    if (!hop) break;
    next = transitionLead(next, hop, now);
    safety += 1;
  }
  const mergedChannels =
    channels && channels.length
      ? Array.from(new Set([...(next.sentChannels ?? []), ...channels]))
      : next.sentChannels ?? null;
  // Phase 3a — EVERY send (a re-send of an already-quoted lead included)
  // restarts the follow-up clock: quote sent → follow up in +2 days.
  return withNextAction(
    { ...next, sentAt: now, sentChannels: mergedChannels, updatedAt: now },
    now,
  );
}

/** The next legal hop from each state on the way to `consult_booked`. */
const HOP_TOWARD_CONSULT: Partial<Record<LeadState, LeadState>> = {
  enquired: 'qualified',
  qualifying: 'qualified',
  qualified: 'consult_booked',
  parked: 'qualifying',
};

/**
 * EVENT: a consult was BOOKED for this lead (the booking seam — today the
 * v1 date-stamp, later the real `bookConsultation` callable). Stamps
 * `consultBookedAt` (date-only ISO) and advances the lead to
 * `consult_booked` via legal spine hops when it sits BEFORE that stage.
 * A lead already at/past consult_booked (or terminal) keeps its state —
 * only the date is stamped (a later-stage lead booking another call must
 * not be dragged backward). Pure — the booking store persists.
 */
export function recordConsultBooked(
  lead: Lead,
  bookedDateISO: string,
  now: string,
): Lead {
  const date = bookedDateISO.slice(0, 10);
  let next: Lead = lead;
  if (!isTerminal(lead.state)) {
    let safety = 0;
    while (next.state !== 'consult_booked' && safety < 4) {
      const hop = HOP_TOWARD_CONSULT[next.state];
      if (!hop) break; // at/past consult_booked on the spine → state stays
      next = transitionLead(next, hop, now);
      safety += 1;
    }
  }
  return { ...next, consultBookedAt: date, updatedAt: now };
}

/** The next legal hop from each state on the way to `discovery_done`. */
const HOP_TOWARD_DISCOVERY: Partial<Record<LeadState, LeadState>> = {
  enquired: 'qualified',
  qualifying: 'qualified',
  qualified: 'discovery_done',
  consult_booked: 'discovery_done',
  parked: 'qualifying',
};

/**
 * EVENT: the discovery call HAPPENED for this lead (the record's "Discovery
 * done" action). Stamps `discoveryDoneAt` (date-only ISO) and advances the
 * lead to `discovery_done` via legal spine hops when it sits BEFORE that
 * stage. A lead already at/past discovery (quoted) or terminal keeps its
 * state — only the date is stamped (never dragged backward). This closes the
 * audit's vocabulary drift: previously the button date-stamped WITHOUT the
 * state moving (a swallowed illegal transition), so the record read
 * "Discovery done" while the board column still read "Consult booked".
 * Pure — the caller persists.
 */
export function recordDiscoveryDone(
  lead: Lead,
  doneDateISO: string,
  now: string,
): Lead {
  const date = doneDateISO.slice(0, 10);
  let next: Lead = lead;
  if (!isTerminal(lead.state)) {
    let safety = 0;
    while (next.state !== 'discovery_done' && safety < 4) {
      const hop = HOP_TOWARD_DISCOVERY[next.state];
      if (!hop) break; // at/past discovery on the spine → state stays
      next = transitionLead(next, hop, now);
      safety += 1;
    }
  }
  return { ...next, discoveryDoneAt: date, updatedAt: now };
}

/* ════════════════════ Recoverable Trash (pure) ════════════════════
 *
 * 2026-05-22 — owner decision: a removed lead goes to a recoverable Trash,
 * never hard-deleted from the client side. These two pure mutators set /
 * clear the `trashed` flag on the `Lead` model; the impure leadStore layer
 * persists the result (the flag round-trips inside the `notes_internal`
 * pipeline snapshot — see the `Lead.trashed` doc comment). Trash is
 * orthogonal to the lead STATE machine — a lead is trashed from ANY state
 * (including a terminal `converted`/`rejected`) and its state is left
 * exactly as it was, so Restore brings the lead back to precisely where it
 * was in the pipeline.
 */

/** PURE — send a lead to the recoverable Trash. State is left untouched. */
export function trashLead(lead: Lead, by: string, now: string): Lead {
  return {
    ...lead,
    trashed: true,
    trashedAt: now,
    trashedBy: by,
    updatedAt: now,
  };
}

/** PURE — restore a trashed lead. It returns to its prior state intact. */
export function restoreLead(lead: Lead, now: string): Lead {
  return {
    ...lead,
    trashed: false,
    trashedAt: null,
    trashedBy: null,
    updatedAt: now,
  };
}

/** PURE — is this lead in the Trash? (defensive — `trashed` is optional). */
export function isTrashed(lead: Lead): boolean {
  return lead.trashed === true;
}

/**
 * Attach a tier + auto-quote and move the lead to `quoted` (Propose &
 * Price). Quoting is only legal from qualified/consult_booked/
 * discovery_done (the state machine enforces the hop). Returns a new
 * lead. The quote value is the LOCKED figure (autoQuote).
 */
export function quoteLead(
  lead: Lead,
  tier: Tier,
  now: string,
  sqftOverride?: number | null,
): Lead {
  const q = autoQuote(tier, sqftOverride ?? lead.sqft);
  const withQuote: Lead = {
    ...lead,
    assignedTier: tier,
    desiredTier: lead.desiredTier ?? tier,
    quote: q,
    updatedAt: now,
  };
  // If not already quoted, advance the spine (legal hop enforced).
  if (lead.state !== 'quoted') {
    return transitionLead(withQuote, 'quoted', now);
  }
  return withQuote;
}

/**
 * Result of converting a lead → client + project. PURE: this computes the
 * bridge payload (ids, project meta, the seed gate/ledger intent). The
 * Firestore layer (described in the report) consumes this to create
 * `projects/{pid}` exactly the way `ensureDemoProject` does — same
 * templateVersion lock + empty gate state + the advance money-gate
 * already created so money-before-delivery is live from conversion. From
 * here the EXISTING Step-6 screens take over unchanged.
 */
export interface ConvertResult {
  lead: Lead;
  projectId: string;
  clientNo: string;
  /** The ProjectMeta-shaped seed the data layer writes to projects/{pid}. */
  projectSeed: {
    vp: string;
    clientName: string;
    clientNo: string;
    tier: string;
    /** Locked Modern+Sanskrit name for the new project. */
    displayName: string;
    /** Total engagement value — the AGREED price when one was set (in the
     *  agreed currency units), else the locked auto-quote standard price. */
    valueINR: number;
    /** First stage of the tier's engagementTemplate. */
    initialStageKey: string;
    /** The advance/first money gate id that gates real work (money-
     *  before-delivery is live the instant the project exists). */
    firstMoneyGateId: string | null;
    /**
     * AGREED-PRICE seeding (2026-06-15) — "whatever price you agree IS what
     * the client pays". When the lead carries an agreed price, the convert
     * seeds the SAME single source of truth the money gate reads:
     *   • `currency`  — the agreed currency (₹/$),
     *   • `gst`       — drives the billing firm,
     *   • `firmId`    — auto-routed from {currency, gst},
     *   • `gateAmounts` — the agreed value distributed across the tier's
     *     money gates (`seedGateAmounts`), so the converted project's gate
     *     expects $40 from the instant it exists (no later re-edit needed).
     * Un-priced / standing-rate convert → currency INR, empty gateAmounts
     * (the template default applies). The data layer writes these straight
     * onto `projects/{pid}` — the canonical `resolveGateAmount` reads them.
     */
    currency: Currency;
    gst: boolean;
    firmId: FirmId;
    gateAmounts: Record<string, number>;
  };
}

/**
 * THE convert-to-project bridge. Legal ONLY from `quoted` (a lead with no
 * locked quote cannot become a money-gated project — money-before-
 * delivery). Deterministic: caller supplies the VP id, client number and
 * `now` (the data layer mints real serial numbers; tests pass fixed
 * values). Produces the project seed the existing pipeline expects — the
 * project's first money gate is taken straight from the tier's LOCKED
 * engagementTemplate (not invented), so the advance gate is created on
 * convert and the Step-6 Money/Ledger/Conversation screens work with zero
 * change.
 */
export function convertLeadToProject(
  lead: Lead,
  projectId: string,
  clientNo: string,
  now: string,
): ConvertResult {
  if (lead.state !== 'quoted') {
    throw new Error(
      `convertLeadToProject: lead ${lead.id} must be 'quoted' (is '${lead.state}')`,
    );
  }
  if (!lead.assignedTier || !lead.quote) {
    throw new Error(
      `convertLeadToProject: lead ${lead.id} has no assigned tier / quote`,
    );
  }
  const tpl = getEngagementTemplate(lead.assignedTier);
  if (!tpl) {
    throw new Error(
      `convertLeadToProject: unknown tier "${lead.assignedTier}"`,
    );
  }
  const firstMoneyGate = tpl.gates.find((g) => g.kind === 'money');
  const converted = transitionLead(
    { ...lead, convertedProjectId: projectId, convertedClientNo: clientNo },
    'converted',
    now,
  );

  // AGREED-PRICE — "whatever price you agree IS what the client pays". When
  // the lead carries an agreed figure, it (and its currency) is the single
  // source of truth seeded onto the project; otherwise fall back to the
  // locked auto-quote standard price in INR (the standing-rate path).
  const hasAgreed =
    typeof lead.agreedValue === 'number' && lead.agreedValue > 0;
  const currency: Currency = lead.agreedCurrency === 'USD' ? 'USD' : 'INR';
  const value = hasAgreed ? Math.round(lead.agreedValue as number) : lead.quote.valueINR;
  const gst =
    typeof lead.gstRequired === 'boolean'
      ? lead.gstRequired
      : currency === 'USD'
        ? false
        : true;
  const firmId: FirmId =
    lead.firmId === 'firm1' || lead.firmId === 'firm2'
      ? lead.firmId
      : firmFor({ currency, gst });
  // Seed the agreed value across the tier's money gates ONLY when an agreed
  // price was set (an empty map → the template default applies, unchanged).
  const gateAmounts = hasAgreed ? seedGateAmounts(lead.assignedTier, value) : {};

  return {
    lead: converted,
    projectId,
    clientNo,
    projectSeed: {
      vp: projectId,
      clientName: lead.name,
      clientNo,
      tier: lead.assignedTier,
      displayName: tpl.displayName,
      valueINR: value,
      initialStageKey: tpl.stages[0]?.key ?? '',
      firstMoneyGateId:
        firstMoneyGate && 'id' in firstMoneyGate
          ? firstMoneyGate.id
          : null,
      currency,
      gst,
      firmId,
      gateAmounts,
    },
  };
}

/**
 * MODULE 1 — the AGREED-PRICE convert gate (Option A, locked 2026-06-17).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (vs `convertLeadToProject`)
 * ─────────────────────────────────────────────────────────────────────────
 *  The legacy bridge `convertLeadToProject` gates on the `lead.quote`
 *  auto-quote object + `state === 'quoted'` (a manual "Lock quote" step).
 *  The owner ruled (2026-06-17) that Convert must gate on the 6 must-haves
 *  + the AGREED PRICE — NOT a separate "Lock quote" tap. This is that path:
 *  it requires a `service` (assignedTier) + an agreed price (or No-charge),
 *  synthesises the `LeadQuote` snapshot from the rate card if one was never
 *  locked, and then DELEGATES to `convertLeadToProject` so the project seed,
 *  the agreed-price → gateAmounts seeding (`resolveGateAmount` spine) and the
 *  ledger entry are produced by the SAME code — no second money path.
 *
 *  It does NOT validate name/email/whatsapp here (those are the screen's 6
 *  must-haves, surfaced live in the UI gate via `leadChecklist`); it only
 *  enforces the two HARD invariants the project seed cannot do without:
 *  a service tier and a money figure (agreed price, or an explicit No-charge).
 *  A No-charge engagement converts at ₹0 (the seed falls back to the
 *  auto-quote standard for the gate split, but the agreed value is 0).
 * ─────────────────────────────────────────────────────────────────────────
 */
export function convertLeadDirect(
  lead: Lead,
  projectId: string,
  clientNo: string,
  now: string,
): ConvertResult {
  if (lead.state === 'converted') {
    throw new Error(
      `convertLeadDirect: lead ${lead.id} is already converted`,
    );
  }
  if (!lead.assignedTier) {
    throw new Error(
      `convertLeadDirect: lead ${lead.id} has no service / tier — pick the service first`,
    );
  }
  const hasAgreed =
    typeof lead.agreedValue === 'number' && lead.agreedValue > 0;
  const noCharge = lead.noCharge === true;
  if (!hasAgreed && !noCharge) {
    throw new Error(
      `convertLeadDirect: lead ${lead.id} has no agreed price — set the price (or mark No charge) first`,
    );
  }

  // Synthesise the LeadQuote snapshot from the rate card when one was never
  // locked, and force the lead onto 'quoted' so the shared bridge accepts it.
  // The agreed-price fields are PRESERVED untouched — `convertLeadToProject`
  // reads `agreedValue`/`agreedCurrency` for the real money seed; this quote
  // object is only the rate-card reference figure the model carries.
  const quote: LeadQuote =
    lead.quote ?? autoQuote(lead.assignedTier, lead.sqft);
  const readyLead: Lead = {
    ...lead,
    quote,
    state: 'quoted',
  };
  // Delegate — ONE seed path. convertLeadToProject does the agreed-price →
  // gateAmounts seeding (the resolveGateAmount spine) and the ledger.
  return convertLeadToProject(readyLead, projectId, clientNo, now);
}

/* ════════════════════ Lead rail (UI scaffold) ════════════════════ */

/**
 * Stage-rail rows for a lead — mirrors engagementTemplate.stageRailFor so
 * the lead detail screen reuses the SAME StageRail component the project
 * screens use (one visual language). Off-ramp states (parked/rejected)
 * render the spine with the rail frozen at the lead's last spine position.
 */
export function leadRail(
  lead: Lead,
): Array<{ label: string; state: 'done' | 'now' | 'todo' }> {
  const spineIdx = LEAD_SPINE.indexOf(lead.state);
  // Off-ramp: not on the spine → freeze at qualifying-ish (index 1) so the
  // rail still reads sensibly; the state chip carries the real status.
  const effectiveIdx = spineIdx >= 0 ? spineIdx : 1;
  return LEAD_SPINE.map((s, i) => ({
    label: LEAD_STATE_LABEL[s],
    state: i < effectiveIdx ? 'done' : i === effectiveIdx ? 'now' : 'todo',
  }));
}

/** Lead age in whole days from createdAt to a supplied `now` ISO. */
export function leadAgeDays(lead: Lead, now: string): number {
  const a = Date.parse(lead.createdAt);
  const b = Date.parse(now);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.floor((b - a) / 86400000));
}

/**
 * MASTER–DETAIL selection contract (chat-first correction batch · item 1).
 * The Lead Inbox is side-by-side master–detail: a list (master) + a
 * triage detail pane. This pure resolver is the single source of truth
 * for "what does the detail pane show" so the layout behaviour is
 * unit-testable (the React screen only renders this result):
 *   · a selected id that still exists in the (filtered) list → that lead
 *   · no selection, or a stale id no longer in view → null (empty pane)
 * Selecting from the list NEVER opens a bottom panel — the detail is the
 * right pane (desktop) / the stacked section (mobile); this resolver is
 * layout-agnostic and just decides the detail subject.
 */
export function resolveDetail(
  leads: Lead[],
  selectedId: string | null,
): Lead | null {
  if (!selectedId) return null;
  return leads.find((l) => l.id === selectedId) ?? null;
}

/** Tier options for the lead-triage picker — the 8 CORE tiers, with
 *  names. Ancillary tiers are not part of the lead funnel (attached to a
 *  project or sold standalone), so they are not offered here. */
export function tierOptions(): Array<{ tier: CoreTier; displayName: string }> {
  return CORE_TIERS.map((t) => ({
    tier: t,
    displayName: getEngagementTemplate(t)!.displayName,
  }));
}

/**
 * PURE — does this lead still need its IDENTITY captured? (2026-07-03,
 * owner: the new-lead name/phone/email capture sat "really deep down below
 * the scroll".) TRUE when the lead has NO reachable channel at all — no
 * phone AND no email — which is exactly the blank manual-entry signature
 * (every automated channel arrives with at least a phone or an email).
 * While this holds the record surfaces the identity capture FIRST; once
 * identity is saved it returns to its normal place. Pure + total.
 */
export function needsIdentityCapture(
  lead: Pick<Lead, 'phone' | 'email'>,
): boolean {
  return !(lead.phone || '').trim() && !(lead.email || '').trim();
}

/**
 * Identity resolver — find an existing contact that matches a phone or email.
 * Normalises phone numbers before comparing (strips +91, 0, spaces, dashes).
 * Phone match takes priority over email match.
 * Returns the first matching contact, or null if none found.
 */
export function findContactByChannel(
  contacts: Array<{
    cid: string;
    name: string;
    clientNo: string;
    phone?: string;
    email?: string;
    phones?: Array<{ number: string; label?: string }>;
    emails?: Array<{ address: string; label?: string }>;
  }>,
  phone?: string,
  email?: string,
): typeof contacts[0] | null {
  function normalisePhone(p: string): string {
    // Remove spaces, dashes, dots, parentheses
    let n = p.replace(/[\s\-().]/g, '');
    // Remove leading +91 or 091
    n = n.replace(/^\+91/, '').replace(/^091/, '');
    // Remove leading 0
    n = n.replace(/^0/, '');
    return n;
  }

  const normPhone = phone ? normalisePhone(phone) : '';
  const normEmail = email ? email.trim().toLowerCase() : '';

  // Phone match first
  if (normPhone) {
    for (const c of contacts) {
      // Check phones[] array
      const arr = c.phones ?? [];
      for (const p of arr) {
        if (normalisePhone(p.number) === normPhone) return c;
      }
      // Check legacy phone field
      if (c.phone && normalisePhone(c.phone) === normPhone) return c;
    }
  }

  // Email match
  if (normEmail) {
    for (const c of contacts) {
      const arr = c.emails ?? [];
      for (const e of arr) {
        if (e.address.trim().toLowerCase() === normEmail) return c;
      }
      if (c.email && c.email.trim().toLowerCase() === normEmail) return c;
    }
  }

  return null;
}
